import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ArrowBack from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import Footer from 'pages/LandingPage/components/Footer';
import Navigation from 'pages/LandingPage/components/Navigation';

interface SuccessStoryData {
  id: number;
  company: string;
  date: string;
  title: string;
  category: string;
  readTime: string;
  context: {
    title: string;
    content: string[];
  };
  challenges: {
    title: string;
    content: string[];
    quotes: string[];
  };
  solution: {
    title: string;
    content: string[];
    quotes: string[];
  };
  conclusion: {
    title: string;
    content: string[];
    quotes?: string[];
    videoUrl?: string;
  };
  impact: {
    title: string;
    metrics?: Array<{
      value: string;
      label: string;
    }>;
  };
}

const SuccessStoryDetail: React.FC = () => {
  const navigate = useNavigate();
  const { storyId } = useParams<{ storyId: string }>();

  const successStoriesData: Record<string, SuccessStoryData> = {
    '1': {
      id: 1,
      company: 'Pompeyo Carrasco',
      date: '22/4/2025',
      title: 'Cómo Pompeyo Carrasco aumentó en un 200% su tasa de cierre',
      category: 'Automotriz',
      readTime: '8 min',
      context: {
        title: 'Contexto',
        content: [
          'Pompeyo Carrasco es una empresa familiar con 58 años de trayectoria en la industria automotriz. Comenzaron como una serviteca, y con el tiempo fueron incorporando marcas referentes del mercado. Hoy, gracias a ese crecimiento sostenido, cuentan con un portafolio de más de 10 marcas de vehículos nuevos, además de una amplia oferta de autos usados. ',
          'Conversamos con Vicente Carrasco, gerente de Marketing y Nuevos Negocios en Pompeyo Carrasco, quien, tras casi dos años utilizando Zeller, nos compartió su experiencia con la plataforma, el impacto que ha tenido en su gestión y los resultados obtenidos hasta la fecha.',
        ],
      },
      challenges: {
        title: 'Problemas y Desafíos',
        content: [
          'Uno de los grandes desafíos actuales en la industria automotriz, es la digitalización, un proceso que poco a poco ha impulsado a los concesionarios a transformar y modernizar sus procesos de venta y servicios. Vivimos en un mundo acelerado, donde los clientes esperan respuestas inmediatas, por lo que el tiempo es un recurso clave hoy en día. ',
          'Tras la pandemia de 2020, el modelo de ventas tradicional dio un giro radical hacia la digitalización. Este cambio los impulsó a buscar nuevas oportunidades comerciales y a adaptarse a esta transformación digital, con el objetivo de incrementar la generación de leads, lograr una contactabilidad inmediata y mejorar la calidad en la conversión de clientes.',
        ],
        quotes: [
          '“Con RRSS y páginas, nosotros al final llegamos a tal punto, donde nos veíamos colapsados con la cantidad de prospectos que teníamos que contactar…. y no éramos capaces.”',
        ],
      },
      solution: {
        title: 'Solución',
        content: [
          'En enero de 2024, Pompeyo Carrasco incorporó Zeller como parte de su transformación digital. Con el apoyo de nuestro agente de inteligencia artificial "Claudio", han optimizado sus procesos comerciales, logrando una mayor eficiencia en la gestión de leads y aumentar el éxito en ventas. La adopción de herramientas digitales no solo optimiza la gestión, sino que también permite acercarse más al cliente, ofreciendo soluciones más ágiles y personalizadas. Gracias a Zeller, el concesionario mantiene hoy una contactabilidad estable del 99% en distintos orígenes.',
          'El equipo de Pompeyo decidió trabajar con nosotros para enfrentar múltiples desafíos, destacando la contactabilidad y asesoría inmediata como el punto fundamental. Con Zeller, se logró cubrir distintas áreas de negocio; partimos con un piloto para atención y venta de autos usados, y hoy abordamos también:',
          '🚗 Venta de autos nuevos y usados.',
          '🎧 Postventa.',
          '💰 Gestión de financiamiento.',
          '🗓️ Agendamiento de visitas.',
          'La integración entre HubSpot y el software de Zeller proporcionaron resultados que permiten hoy demostrar el verdadero valor que brinda esta herramienta. En estos dos años de trabajo conjunto, Pompeyo Carrasco ha mejorado significativamente sus tasas de conversión, donde Vicente nos declara que la tasa de cierre en distintos canales creció en un 200%, un resultado que definió como un gran éxito.',
        ],
        quotes: [
          '“Hoy día Claudio (IA) lo puede hacer todo. El gran éxito de esta herramienta y de Claudio, es que los dos juntos de la mano, hemos ido creyendo y confiando que Claudio lo puede hacer todo”',
        ],
      },
      conclusion: {
        title: 'Resultados',
        content: [
          'Los resultados obtenidos por Pompeyo Carrasco con Zeller han sido excepcionales. La tasa de cierre aumentó en un 200% en diferentes canales, demostrando el verdadero impacto de la inteligencia artificial en el proceso comercial.',
          'La transformación digital de Pompeyo Carrasco con Zeller es un ejemplo claro de cómo la tecnología puede potenciar el crecimiento de un negocio familiar tradicional, manteniendo siempre el foco en la excelencia del servicio al cliente.',
        ],
        quotes: [
          '"Hoy, muchos clientes llegan preguntando por Claudio, convencidos de que es un vendedor real. Esta anécdota demuestra el nivel de servicio y realismo que ofrece Zeller.”',
        ],
        videoUrl: 'https://www.youtube.com/watch?v=7iCQGSzf2fA',
      },
      impact: {
        title: 'Impacto y Resultados',
        metrics: [
          { value: '48%', label: 'Conversaciones fuera de horario laboral' },
          { value: '24/7', label: 'Atención continua' },
          { value: '99%', label: 'Tasa de contactabilidad' },
        ],
      },
    },
    '2': {
      id: 2,
      company: 'Salfa',
      date: '19/6/2025',
      title: 'Cómo Salfa mejoró su eficiencia y aumentó sus tasas de cierre',
      category: 'Automotriz',
      readTime: '5 min',
      context: {
        title: 'Contexto',
        content: [
          'Salfa Automotriz es uno de los concesionarios más importantes de Chile, con más de 85 años de trayectoria y más de 2.300 colaboradores. Cuenta con operaciones en vehículos livianos, camiones, maquinaria, motores industriales y más. Además, ofrece arriendo de vehículos tanto a largo plazo (leasing operativo para empresas) como a corto plazo, a través de su servicio de rent a car diario con más de 20.000 vehículos activos en el país.',
          'En su camino a la transformación digital, Salfa identificó una oportunidad para optimizar su canal de atención online, el cual recibía una alta cantidad de leads desde diversos orígenes. Esta creciente demanda planteaba el desafío de mantener una atención ágil, eficiente y oportuna para todos los clientes digitales.',
          'Entrevistamos a Anita Rodriguez, subgerente de canales digitales, quien nos cuenta, luego de 1 año y medio trabajando en conjunto, sobre Salfa y la experiencia que ha tenido junto a Zeller, el impacto en sus vendedores y la implementación de la plataforma dentro del concesionario. ',
        ],
      },
      challenges: {
        title: 'El Problema',
        content: [
          'Antes de implementar Zeller, el equipo de Salfa enfrentaba dos grandes desafíos:',
          '1. Alto volumen de leads sin contacto oportuno e inmediato por parte de la fuerza de ventas.',
          '2. Tampoco se lograba cobertura completa de los leads generados en los canales digitales, implicando pérdida de oportunidades e ineficiencias en el proceso comercial inicial.',
          'Además, tras probar soluciones con IA más rígidas y con respuestas poco naturales, Salfa necesitaba una herramienta que entregara una experiencia conversacional más humana, sin sobrecargar al equipo comercial.',
        ],
        quotes: [
          '"Estábamos buscando un solución tecnológica que nos ayudara a ser más eficientes y también, de cara a clientes, crear una mejor experiencia dando una respuesta inmediata."',
        ],
      },
      solution: {
        title: 'La Solución: Zeller en acción',
        content: [
          'Con Zeller, Salfa logró automatizar y optimizar su proceso de atención digital, integrando la IA en distintos puntos clave:',
          ' - Recepción y contacto automático de leads.',
          ' - Análisis de conversaciones y datos para priorizar según intención de compra.',
          ' - Enriquecimiento de datos directamente en el CRM.',
          'Esto permitió una respuesta mucho más rápida al cliente, liberando carga operativa a los vendedores, quienes ahora se enfocan solo en los leads con mayor potencial de conversión. Además, los leads poco calificados o con dudas simples son atendidos automáticamente por nuestra IA, generando eficiencia en todo el funnel de atención.',
          'El proceso de implementación fue altamente colaborativo y ágil, gracias a nuestra capacidad de adaptarnos a los múltiples sistemas internos de Salfa. Anita nos menciona, cómo nuestro equipo de soporte se destaca por su proactividad, conocimiento técnico para integrarse y cercanía con el negocio.',
        ],
        quotes: [
          '"El proceso de implementación fue altamente colaborativo y ágil, gracias a nuestra capacidad de adaptarnos a los múltiples sistemas internos de Salfa. Anita nos menciona, cómo nuestro equipo de soporte se destaca por su proactividad, conocimiento técnico para integrarse y cercanía con el negocio.',
        ],
      },
      conclusion: {
        title: 'Próximos Pasos',
        content: [
          'Salfa ya está evaluando nuevas áreas de aplicación para la IA, incluyendo postventa, atención al cliente y gestión de campañas. El objetivo es claro: seguir ganando eficiencia y automatizando tareas repetitivas para escalar el valor de Zeller en toda la organización.',
          'Les dejamos un pequeño video con una anécdota de Marcos, el agente IA de Zeller en Salfa:',
        ],
        videoUrl: 'https://www.youtube.com/watch?v=rXzoPJYyDLk',
      },
      impact: {
        title: 'Impacto y Resultados',
        metrics: [
          { value: '52,7%', label: 'Contactados fuera de horario laboral' },
          { value: '99%', label: 'Leads contactados' },
        ],
      },
    },
  };

  const story = storyId ? successStoriesData[storyId] : null;

  const getYouTubeVideoId = (url: string): string | null => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  if (!story) {
    return (
      <>
        <Navigation />
        <Container maxWidth="lg" sx={{ py: 20 }}>
          <Typography variant="h4" color="error" textAlign="center">
            Caso de éxito no encontrado
          </Typography>
          <Box textAlign="center" mt={4}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/casos-de-exito')} variant="outlined">
              Volver a casos de éxito
            </Button>
          </Box>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navigation />

      <Box sx={{ pt: 20, pb: 8, backgroundColor: '#15183A' }}>
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/casos-de-exito')}
            sx={{
              color: 'white',
              mb: 4,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Volver a casos de éxito
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Chip
              label={story.category}
              sx={{
                backgroundColor: '#F9D00A',
                color: '#15183A',
                fontWeight: 'medium',
              }}
            />
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {story.readTime} de lectura • {story.date}
            </Typography>
          </Box>

          <Typography
            variant="h2"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              mb: 3,
              fontSize: { xs: '2rem', md: '3rem' },
              lineHeight: 1.2,
            }}
          >
            {story.title}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  color: '#15183A',
                  fontWeight: 'bold',
                  mb: 4,
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                }}
              >
                {story.context.title}
              </Typography>
              {story.context.content.map((paragraph) => (
                <Typography
                  key={`context-${paragraph.substring(0, 30).replace(/\s+/g, '-')}`}
                  variant="body1"
                  sx={{
                    mb: 3,
                    fontSize: '1.1rem',
                    lineHeight: 1.7,
                    color: '#333',
                  }}
                >
                  {paragraph}
                </Typography>
              ))}
            </Box>

            <Box>
              <Typography
                variant="h3"
                sx={{
                  color: '#15183A',
                  fontWeight: 'bold',
                  mb: 4,
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                }}
              >
                {story.challenges.title}
              </Typography>
              {story.challenges.content.map((paragraph) => (
                <Typography
                  key={`challenges-${paragraph.substring(0, 30).replace(/\s+/g, '-')}`}
                  variant="body1"
                  sx={{
                    mb: 3,
                    fontSize: '1.1rem',
                    lineHeight: 1.7,
                    color: '#333',
                  }}
                >
                  {paragraph}
                </Typography>
              ))}

              {story.challenges.quotes && story.challenges.quotes.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  {story.challenges.quotes.map((quote) => (
                    <Box
                      key={`challenges-quote-${quote.substring(0, 30).replace(/\s+/g, '-')}`}
                      sx={{
                        borderLeft: '4px solid #F9D00A',
                        pl: 3,
                        py: 2,
                        mb: 3,
                        backgroundColor: '#f8f9fa',
                        borderRadius: '0 8px 8px 0',
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '1.1rem',
                          lineHeight: 1.7,
                          color: '#15183A',
                          fontStyle: 'italic',
                          fontWeight: 'medium',
                        }}
                      >
                        {quote}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                backgroundColor: '#F9D00A',
                borderRadius: 3,
                p: 4,
                height: 'fit-content',
                position: 'sticky',
                top: 20,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: '#15183A',
                  fontWeight: 'bold',
                  mb: 3,
                  fontSize: { xs: '1.5rem', md: '1.75rem' },
                }}
              >
                {story.impact.title}
              </Typography>

              <Box sx={{ mb: 4 }}>
                {story.impact.metrics?.map((metric) => (
                  <Box key={`metric-${metric.value}-${metric.label.replace(/\s+/g, '-')}`} sx={{ mb: 3 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        color: '#15183A',
                        fontWeight: 'bold',
                        fontSize: '2.5rem',
                        lineHeight: 1,
                        mb: 0.5,
                      }}
                    >
                      {metric.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#15183A',
                        fontWeight: 'medium',
                        fontSize: '0.95rem',
                      }}
                    >
                      {metric.label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: '#15183A',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#0f1129',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(21, 24, 58, 0.3)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => {
                    window.open('https://zcal.co/i/O1PPkO_D', '_blank');
                  }}
                >
                  Solicitar una demo gratuita
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    color: '#15183A',
                    mt: 1,
                    fontSize: '0.8rem',
                    opacity: 0.8,
                  }}
                >
                  Descubre cómo Zeller puede transformar tu negocio
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 6 }} />

        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h3"
            sx={{
              color: '#15183A',
              fontWeight: 'bold',
              mb: 4,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
            }}
          >
            {story.solution.title}
          </Typography>

          {story.solution.content.map((paragraph) => (
            <Typography
              key={`solution-${paragraph.substring(0, 30).replace(/\s+/g, '-')}`}
              variant="body1"
              sx={{
                mb: 3,
                fontSize: '1.1rem',
                lineHeight: 1.7,
                color: '#333',
              }}
            >
              {paragraph}
            </Typography>
          ))}

          {story.solution.quotes && story.solution.quotes.length > 0 && (
            <Box sx={{ mt: 4 }}>
              {story.solution.quotes.map((quote) => (
                <Box
                  key={`solution-quote-${quote.substring(0, 30).replace(/\s+/g, '-')}`}
                  sx={{
                    borderLeft: '4px solid #F9D00A',
                    pl: 3,
                    py: 2,
                    mb: 3,
                    backgroundColor: '#f8f9fa',
                    borderRadius: '0 8px 8px 0',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1.1rem',
                      lineHeight: 1.7,
                      color: '#15183A',
                      fontStyle: 'italic',
                      fontWeight: 'medium',
                    }}
                  >
                    {quote}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 6 }} />
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h3"
            sx={{
              color: '#15183A',
              fontWeight: 'bold',
              mb: 4,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
            }}
          >
            {story.conclusion.title}
          </Typography>
          {story.conclusion.content.map((paragraph) => (
            <Typography
              key={`conclusion-${paragraph.substring(0, 30).replace(/\s+/g, '-')}`}
              variant="body1"
              sx={{
                mb: 3,
                fontSize: '1.1rem',
                lineHeight: 1.7,
                color: '#333',
              }}
            >
              {paragraph}
            </Typography>
          ))}

          {story.conclusion.quotes && story.conclusion.quotes.length > 0 && (
            <Box sx={{ mt: 4 }}>
              {story.conclusion.quotes.map((quote) => (
                <Box
                  key={`conclusion-quote-${quote.substring(0, 30).replace(/\s+/g, '-')}`}
                  sx={{
                    borderLeft: '4px solid #F9D00A',
                    pl: 3,
                    py: 2,
                    mb: 3,
                    backgroundColor: '#f8f9fa',
                    borderRadius: '0 8px 8px 0',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1.1rem',
                      lineHeight: 1.7,
                      color: '#15183A',
                      fontStyle: 'italic',
                      fontWeight: 'medium',
                    }}
                  >
                    {quote}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {story.conclusion.videoUrl && (
            <Box sx={{ mt: 4, mb: 4 }}>
              {(() => {
                const videoId = getYouTubeVideoId(story.conclusion.videoUrl);
                if (videoId) {
                  return (
                    <Box
                      sx={{
                        position: 'relative',
                        paddingBottom: '56.25%',
                        height: 0,
                        overflow: 'hidden',
                        maxWidth: '100%',
                        backgroundColor: '#000',
                        borderRadius: 2,
                      }}
                    >
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title="Video de caso de éxito"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          border: 'none',
                        }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </Box>
                  );
                }
                return null;
              })()}
            </Box>
          )}
        </Box>
      </Container>

      <Footer />
    </>
  );
};

export default SuccessStoryDetail;
